import {
	BEHAVIOUR_PATH_ORBIT,
	BEHAVIOUR_REPELL,
	PATH_FLAG_SCALE_ACTIVE,
	REPELL_PUSH,
	VEC_X,
	VEC_Y,
	writeVectorConfig
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { LOOKUP_CIRCLE, getPathLookups } from 'app/views/home/webgl/particlesOptimised/core/pathLookups';
import { GROUP_SPACES } from 'app/views/home/webgl/particlesOptimised/departments/spaces/SpacesContext';

import type { SpacesContext } from 'app/views/home/webgl/particlesOptimised/departments/spaces/SpacesContext';
import type { DepartmentPhase } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * SquarePathsPhase — FORK of SpacesDepartment._setSquarePaths (+ the
 * squarePaths branch of SpacesParticleProcessor, and its processor-tick
 * _doExpandChange walk).
 *
 * VISUAL: every particle orbits the box centre on the circle lookup at one
 * of 19 quantised radius steps ("square" rings of jumps 29x21), additive
 * blend, initially loose (ease 20-40) then snapping tighter as a 1s counter
 * ease-lerps the eases down to ~4-9 over 60 ticks; the whole formation
 * slowly CONTRACTS as the shared expand multiplier walks down from 1
 * (accelerating: amount *= 1.012 per step).
 *
 * The expand walk is per-particle state in the reference, but every armed
 * particle carries identical values ticked with the same deltaTime —
 * lockstep — so it is ONE shared accumulator here with a batch expandTarget
 * write (value-identical). The reference only ever exits this stage into
 * RandomLerpLines, whose per-particle arm calls _reset (doExpandChange off)
 * — hide() clearing the walk is output-identical for the shipped cycle.
 *
 * _pathPhases keeps its reference shape: a single config whose cursor
 * (_lastPathIndex) advances per arm and wraps — deliberately PERSISTENT
 * across scene stops like the reference field (reset() leaves it).
 */
export class SquarePathsPhase implements DepartmentPhase {
	public readonly name = 'SquarePaths';

	private _ctx: SpacesContext;

	//_pathPhases (x/y written at arm — frame centre):
	private _pathPhases: Array<{
		lookup: number;
		speedM: number;
		expandM: number;
		doExpandChange: boolean;
		x?: number;
		y?: number;
	}> = [{ lookup: LOOKUP_CIRCLE, speedM: 0.2, expandM: 0.7, doExpandChange: true }];
	private _lastPathIndex = 0;

	//The shared expand walk (see header):
	private _doExpandChange = false;
	private _expandM = 1;
	private _expandMAmount = 0.0005;
	private _deltaCounter = 0;

	constructor(ctx: SpacesContext) {
		this._ctx = ctx;
	}

	public init(): void {
		//No one-time setup.
	}

	public start(): void {
		const config = this._pathPhases[this._lastPathIndex];
		const frame = this._ctx.getFrameSizes();

		config.x = frame.xc;
		config.y = frame.yc;

		this._expandM = 1;
		this._expandMAmount = 0.0005;
		this._doExpandChange = config.doExpandChange;
		this._deltaCounter = 0;

		for (let n = 0; n < this._ctx.activeCount; n++) {
			this._squarePathLane(this._ctx.range.start + n, config);
		}

		this._lastPathIndex++;
		if (this._lastPathIndex > this._pathPhases.length - 1) {
			this._lastPathIndex = 0;
		}
	}

	/** The processor tick's _doExpandChange walk (shared lockstep state). */
	public tick(): void {
		if (!this._doExpandChange) {
			return;
		}

		this._deltaCounter += this._ctx.deps.context.deltaTime;

		if (this._deltaCounter >= 1) {
			this._deltaCounter--;

			if (this._expandM > 0) {
				this._expandM -= this._expandMAmount;
				this._expandMAmount *= 1.012;
			}

			const orbit = this._ctx.core.orbitPath;
			for (let n = 0; n < this._ctx.activeCount; n++) {
				orbit.expandTarget[this._ctx.range.start + n] = this._expandM;
			}
		}
	}

	public hide(): void {
		this._doExpandChange = false;
	}

	public reset(): void {
		//_lastPathIndex is deliberately persistent (reference field — never
		//reset across scene stops).
	}

	/** SpacesParticleProcessor.squarePaths, lane-for-line. */
	private _squarePathLane(
		lane: number,
		config: { lookup: number; speedM: number; expandM: number; x?: number; y?: number }
	): void {
		const ctx = this._ctx;
		const core = ctx.core;
		const orbit = core.orbitPath;

		ctx.resetLane(lane);
		ctx.deactivateAll(lane);

		const x = config.x || 0;
		const y = config.y || 0.36;
		const expandM = config.expandM || 1;

		//position.set({x, y}) — SOFT:
		core.relX[lane] = x;
		core.relY[lane] = y;

		writeVectorConfig(core, lane, VEC_X, false);
		writeVectorConfig(core, lane, VEC_Y, false);

		orbit.lookupId[lane] = config.lookup;
		core.behaviourMask[lane] |= BEHAVIOUR_PATH_ORBIT;

		ctx.setEaseXY(lane, 20 + Math.random() * 20, 20 + Math.random() * 20);

		//counter (1s): setEase({x: 4+r*5, y: 4+r*4}, true, 60) — the ease
		//VALUE lerps over 60 ticks (Inertia.lerpEase; skipped per axis when
		//the diff is 0, like the reference):
		ctx.deps.scripts.addMs(
			lane,
			1000,
			(l: number) => {
				const targetX = 4 + Math.random() * 5;
				const targetY = 4 + Math.random() * 4;
				const diffX = targetX - core.easeX[l];
				const diffY = targetY - core.easeY[l];
				if (diffX !== 0) {
					core.easeLerpStepX[l] = diffX / 60;
					core.easeLerpFramesX[l] = 60;
				}
				if (diffY !== 0) {
					core.easeLerpStepY[l] = diffY / 60;
					core.easeLerpFramesY[l] = 60;
				}
			},
			false,
			GROUP_SPACES
		);

		const scaler = Math.round(Math.random() * 18);
		const jumpsX = 29;
		const jumpsY = 21;
		orbit.xMul[lane] = (1 + scaler * jumpsX + Math.random() * (0.7 * scaler)) * expandM;
		orbit.yMul[lane] = (1 + scaler * jumpsY + Math.random() * (0.7 * scaler)) * expandM;
		orbit.flags[lane] &= ~PATH_FLAG_SCALE_ACTIVE;

		core.tScale[lane] = 0.06 + 0.008 * scaler;
		core.tAlpha[lane] = 0.2 + Math.random() * 0.8;
		core.blendAdd[lane] = 1;

		orbit.speed[lane] = (0.5 + Math.random() * 4 + 0.1 * scaler) * config.speedM;
		//randomise(false): pathIndex = scaleIndex = rand * (len - 1):
		const tableLength = getPathLookups()[orbit.lookupId[lane]].length;
		orbit.idx[lane] = orbit.scaleIdx[lane] = Math.random() * (tableLength - 1);

		core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
		core.repellType[lane] = REPELL_PUSH;
		core.repellRadius[lane] = 100;
		core.repellPower[lane] = 100 + Math.random() * 20;
	}
}
